import { type MouseEvent, type Ref, useState } from 'react'
import { Link } from 'react-router-dom'

type NavTabProps = {
  path: string
  label: string
  isActive: boolean
  isExact: boolean
  // Forwarded to the underlying <a> so NavTabs can measure this tab's geometry for the
  // shared sliding underline.
  innerRef?: Ref<HTMLAnchorElement>
}

/**
 * A header nav tab: label plus an on-hover ghost underline growing from the cursor's entry point.
 * The ACTIVE tab's green underline is not here — NavTabs owns it as a single measured slider.
 */
export default function NavTab({ path, label, isActive, isExact, innerRef }: NavTabProps) {
  // Transform-origin for the ghost underline's scale-x, tracking cursor entry.
  // Defaults to center so a keyboard-focus/hover with no pointer still looks right.
  const [originX, setOriginX] = useState('50%')

  const handleEnter = (event: MouseEvent<HTMLAnchorElement>) => {
    const { left, width } = event.currentTarget.getBoundingClientRect()
    const x = event.clientX - left
    setOriginX(`${Math.max(0, Math.min(width, x))}px`)
  }

  return (
    <Link
      ref={innerRef}
      to={path}
      onMouseEnter={handleEnter}
      // Deliberately NO padding: NavTabs measures the underline from this anchor's own
      // getBoundingClientRect, so horizontal padding would widen it past the label. A ring changes no geometry.
      className={`group relative rounded-sm text-base font-light transition-colors duration-300 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-pelorous-500 dark:focus-visible:ring-pelorous-300 focus-visible:ring-offset-2 focus-visible:ring-offset-background ${isActive ? `text-brand-green${isExact ? ' pointer-events-none' : ''}` : 'text-muted-foreground hover:text-foreground'}`}>
      {label}
      {!isActive && (
        <span
          style={{ transformOrigin: `${originX} center` }}
          className="absolute -bottom-2 left-0 right-0 h-0.5 scale-x-0 rounded-full bg-foreground/30 transition-transform duration-200 ease-out group-hover:scale-x-100"
        />
      )}
    </Link>
  )
}
